export function parseCsvLine(line) {
  const values = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      values.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current.trim());
  return values;
}

export function parseTournamentCsv(csv) {
  const lines = String(csv || '').trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const tournaments = [];
  for (let i = 1; i < lines.length; i++) {
    const v = parseCsvLine(lines[i]);
    if (v.length >= 2 && v[1]) {
      tournaments.push({ date: v[0] || '', name: v[1] || '', location: v[2] || '', tier: v[3] || '', url: v[4] || '' });
    }
  }
  return tournaments;
}

const TOURNAMENT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TOURNAMENT_MONTH_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

export function parseTournamentDate(raw, now = new Date()) {
  if (!raw) return null;
  const match = String(raw).match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s*(\d{4})?/i);
  if (!match) return null;
  const monthIndex = TOURNAMENT_MONTH_INDEX[match[1].substring(0, 3).toLowerCase()];
  if (monthIndex == null) return null;
  const day = parseInt(match[2], 10);
  const year = match[3] ? parseInt(match[3], 10) : now.getFullYear();
  const dateObj = new Date(year, monthIndex, day);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return {
    month: TOURNAMENT_MONTHS[monthIndex],
    day,
    year,
    dateObj,
    isPast: dateObj < today,
  };
}

export function upcomingTournaments(tournaments, now = new Date()) {
  return (Array.isArray(tournaments) ? tournaments : [])
    .map((tournament) => ({ tournament, date: parseTournamentDate(tournament && tournament.date, now) }))
    .filter((row) => row.date && !row.date.isPast)
    .sort((a, b) => a.date.dateObj - b.date.dateObj)
    .map((row) => row.tournament);
}

export function parseHomepageEventCsv(csv) {
  const lines = String(csv || '').trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
  const events = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]);
    const event = {};
    headers.forEach((h, j) => {
      event[h] = values[j] ? values[j].trim() : '';
    });
    if (String(event.active).toUpperCase() !== 'FALSE' && event.title && event.date) events.push(event);
  }
  return events;
}

// Club-business items (meetings, minutes, fundraisers, socials) belong under "Club Events", not the
// homepage "Events" list — which is for disc golf tournaments & league rounds. Mirrors the worker's
// classifier in auth-worker/src/feeds.ts so Crotts and the site agree on the split.
const CLUB_EVENT_RE = /\b(meeting|minutes|agenda|fundrais|donat|social|banquet|cleanup|clean-up|volunteer|board|election|membership drive|potluck|holiday party)\b/i;
export function isClubEvent(event) {
  return CLUB_EVENT_RE.test((((event && event.title) || '') + ' ' + ((event && event.description) || '')));
}

function inferYearlessDate(month, day, now) {
  const year = now.getFullYear();
  const candidate = new Date(year, month, day);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (candidate >= today) return candidate;
  if (now.getMonth() >= 9 && month <= 2) return new Date(year + 1, month, day);
  return candidate;
}

export function parseHomepageEventDate(raw, now = new Date()) {
  if (!raw || typeof raw !== 'string') return tbdDate();
  const s = raw.trim();
  if (!s || s.toUpperCase() === 'TBD') return tbdDate();
  const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = {
    jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4, jun: 5, june: 5,
    jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8, oct: 9, october: 9,
    nov: 10, november: 10, dec: 11, december: 11,
  };
  let date = null;
  let match = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (match) date = new Date(+match[1], +match[2] - 1, +match[3]);
  if (!date) {
    match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (match) {
      let y = +match[3];
      if (y < 100) y += y < 50 ? 2000 : 1900;
      date = new Date(y, +match[1] - 1, +match[2]);
    }
  }
  if (!date) {
    match = s.match(/^(\d{1,2})[/-](\d{1,2})$/);
    if (match) {
      date = inferYearlessDate(+match[1] - 1, +match[2], now);
    }
  }
  if (!date) {
    match = s.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/);
    if (match) {
      const mi = monthIndex[match[1].toLowerCase()];
      if (mi !== undefined) date = new Date(+match[3], mi, +match[2]);
    }
  }
  if (!date) {
    match = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
    if (match) {
      const mi = monthIndex[match[2].toLowerCase()];
      if (mi !== undefined) date = new Date(+match[3], mi, +match[1]);
    }
  }
  if (!date && /^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (n > 1 && n < 100000) date = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
  }
  if (!date || Number.isNaN(date.getTime())) {
    const fallback = new Date(s);
    if (!Number.isNaN(fallback.getTime())) date = fallback;
  }
  if (!date || Number.isNaN(date.getTime())) return tbdDate();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return { day: date.getDate(), month: monthsShort[date.getMonth()], year: date.getFullYear(), isPast: date < today, isTBD: false, dateObj: date };
}

function hasExplicitFeedYear(raw) {
  const s = String(raw || "").trim();
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(s)) return true;
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(s)) return true;
  if (/^[A-Za-z]+\s+\d{1,2},?\s+\d{4}/.test(s)) return true;
  if (/^\d{1,2}\s+[A-Za-z]+\s+\d{4}/.test(s)) return true;
  return false;
}

/** Prefer a calendar date that names its year (Tee Sign Fund 2026-06-01) over a last-updated epoch.
 *  Bare M/D values like 5/14 keep the epoch so Club Meeting heuristics still work. */
export function feedDateInfo(item, now = new Date()) {
  const raw = String(item?.date || "");
  if (hasExplicitFeedYear(raw)) return parseHomepageEventDate(raw, now);
  const epoch = Number(item?.epoch);
  if (Number.isFinite(epoch) && epoch > 0) {
    const ms = epoch < 100000000000 ? epoch * 1000 : epoch;
    const fromEpoch = new Date(ms);
    if (!Number.isNaN(fromEpoch.getTime())) {
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return { dateObj: fromEpoch, isPast: fromEpoch < today, isTBD: false };
    }
  }
  return parseHomepageEventDate(raw, now);
}

function tbdDate() {
  return { day: 'TBD', month: '', year: '', isPast: false, isTBD: true, dateObj: new Date(9999, 11, 31) };
}
